'use client';

import Conditional from '@next-app/components/Conditional';
import LLCheckbox from '@next-app/components/elements/LLCheckbox/LLCheckbox';
import LLInput from '@next-app/components/elements/LLInput/LLInput';
import {
  selectBillingAddress,
  selectCheckoutDetails,
  selectCreditCardFormErrors,
  selectDefaultProfileAddress,
  selectIsShipOrder,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import {
  setBillingAddressError,
  setBillingRequest,
  setCreditCardFormError,
  setIsSubmitOrder,
} from '@next-app/lib/features/checkout/CheckoutSlice';
import { useDispatch, useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

const CVVHelpModal = dynamic(() => import('../CVVHelpModal/CVVHelpModal'), {
  ssr: true,
});

import Image from 'next/image';
import {
  CHECKOUT_VALIDATION_MESSAGES,
  FIELD_VALIDATION_RULES,
  PAYMENT,
} from '@next-app/constants/CheckoutConstants';
import ExpirationDateInput from './ExpirationDateInput';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import {
  CreditCardFormFields,
  CreditCardFormInitialState,
} from './CreditCardFormFields';
import { isNotEmptyObject } from '@next-app/utils/common-utils';
import { BillingAddressFormFields } from '../BillingAddressForm/BillingAddressFormFields';
import {
  maskGiftCardNumber,
  sendOnBlurDTMEvent,
} from '@next-app/utils/CheckoutHelper';

const CCCardDetailsForm = ({
  editBillingAddress,
  setEditBillingAddress,
  cardInfo,
  keyVal,
  savePayment = false,
  saveAsDefault = false,
  isDeliveryAdd,
  setIsDeliveryAdd,
  displaySaveCancelButtons,
}: {
  editBillingAddress?: boolean;
  setEditBillingAddress?: Function;
  cardInfo?: any;
  keyVal: string;
  savePayment?: boolean;
  saveAsDefault?: boolean;
  setIsDeliveryAdd: Function;
  isDeliveryAdd: boolean;
  displaySaveCancelButtons?: boolean;
}) => {
  const isShipOrder = useSelector(selectIsShipOrder);
  const billingAddressData = useSelector(selectBillingAddress);
  const creditCardFormErrors = useSelector(selectCreditCardFormErrors);
  const defaultProfileAddress = useSelector(selectDefaultProfileAddress);

  const {
    registryAddressSelected = false,
    clearTheListAddressSelected = false,
  } = useSelector(selectCheckoutDetails);

  const dispatch = useDispatch();
  const [cardType, setCardType] = useState('');
  const [cardImg, setCardImg] = useState('');
  const [cardNumberLength, setCardNumberLength] = useState(16);
  const [cvvLength, setCvvLength] = useState(3);
  const [formData, setFormData] = useState<Record<string, any>>(
    CreditCardFormInitialState,
  );
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [expDate, setExpDate] = useState('');

  useEffect(() => {
    fetchExpiryDate();
    if (keyVal !== PAYMENT.ADD_NEW_CC && isNotEmptyObject(cardInfo)) {
      setCardType(cardInfo.creditCardType);
      const cvvLen =
        cardInfo.creditCardType === PAYMENT.CREDIT_CARD_TYPE.AMERICAN_EXPRESS
          ? 4
          : 3;
      setCvvLength(cvvLen);
      const cardImg =
        handleCardIcons(cardInfo.creditCardType) ||
        handleCardIcons(PAYMENT.CREDIT_CARD_TYPE.VISA);
      setCardImg(cardImg);
    }
  }, []);

  useEffect(() => {
    if (isNotEmptyObject(creditCardFormErrors)) {
      setFormErrors(creditCardFormErrors);
    }
  }, [creditCardFormErrors]);

  useEffect(() => {
    const isFormValidate = validateForm(formErrors, formData);
    const fullYear =
      formData.expirationYear && formData.expirationYear < 100
        ? 2000 + formData.expirationYear
        : formData.expirationYear;
    dispatch(
      setBillingRequest({
        creditCardFormData: {
          accountNumber: '',
          cardHolderName: '',
          expirationMonth: formData.expirationMonth,
          expirationYear: +fullYear,
          creditCardNumber: formData.creditCardNumber,
          poNumber: '',
          saveAsDefault: saveAsDefault,
          savePayment: savePayment,
          cardVerificationNumber: formData.cardVerificationNumber,
          paymentMethod: PAYMENT.CREDIT_CARD,
          useShippingAddress: isDeliveryAdd || false,
          creditCardType: cardType,
        },
      }),
    );
    if (isFormValidate) {
      dispatch(setIsSubmitOrder(true));
    }
    if (isNotEmptyObject(formErrors)) {
      dispatch(setCreditCardFormError(formErrors));
    }
  }, [formErrors, formData, isDeliveryAdd]);

  useEffect(() => {
    if (isDeliveryAdd) {
      dispatch(
        setBillingRequest({
          billingAddressFormData:
            cardInfo?.billingAddress || defaultProfileAddress,
        }),
      );
    }
  }, [defaultProfileAddress]);

  const handleCardIcons = (cardType: string) => {
    const cardIcons: { [key: string]: string } = {
      Visa: PAYMENT.CREDIT_CARD_IMG.Visa,
      Mastercard: PAYMENT.CREDIT_CARD_IMG.Mastercard,
      AmericanExpress: PAYMENT.CREDIT_CARD_IMG.AmericanExpress,
      Discover: PAYMENT.CREDIT_CARD_IMG.Discover,
    };
    return cardIcons[cardType];
  };

  const fetchExpiryDate = () => {
    let year;
    let month;
    if (keyVal !== PAYMENT.ADD_NEW_CC) {
      if (cardInfo.notifyDate) {
        let notifyDateTime = cardInfo.notifyDate.time + 172800000;

        const date = new Date(notifyDateTime);
        month = String(date.getMonth() + 1).padStart(2, '0');
        const fullYear = date.getFullYear();
        year = fullYear % 100;
        setExpDate(`${month}/${year}`);
      }
      setFormData({
        [CreditCardFormFields.CC_NUMBER]: cardInfo?.creditCardNumber,
        [CreditCardFormFields.CARD_CVV]: '',
        [CreditCardFormFields.EXPIRATION_MONTH]: month,
        [CreditCardFormFields.EXPIRATION_YEAR]: year,
      });
    }
  };

  const validateForm = (formError: any, formData: any) => {
    const isFormErrorValid =
      Object.keys(formError).length === 0 ||
      Object.values(formError).every((error) => error === '');
    // Check formData: every key must have a non-empty value
    const isFormDataValid = Object.values(formData).every(
      (value) => value !== '' && value !== null && value !== undefined,
    );

    const requiredBillingFields = [
      BillingAddressFormFields.FIRST_NAME,
      BillingAddressFormFields.LAST_NAME,
      BillingAddressFormFields.COUNTRY,
      BillingAddressFormFields.CITY,
      BillingAddressFormFields.STATE,
      BillingAddressFormFields.POSTAL_CODE,
      BillingAddressFormFields.ADDRESS_ONE,
      BillingAddressFormFields.PHONE_NO,
    ];

    let isBillingAddressValid = false;
    // Check billingAddressData: all required fields must have non-empty values
    if (isNotEmptyObject(billingAddressData.billingAddressFormData)) {
      isBillingAddressValid = requiredBillingFields.every((field) => {
        const value = billingAddressData?.billingAddressFormData[field];
        return value !== '' && value !== null && value !== undefined;
      });
    }

    isBillingAddressValid =
      isBillingAddressValid ||
      (!registryAddressSelected &&
        !clearTheListAddressSelected &&
        isDeliveryAdd);

    return (
      isFormErrorValid &&
      isFormDataValid &&
      isBillingAddressValid &&
      !displaySaveCancelButtons
    );
  };

  function getCardType(event: ChangeEvent) {
    handleFormUpdates(CreditCardFormFields.CC_NUMBER, event);
    const cardNumber = (event.target as HTMLInputElement).value.replace(
      /\s/g,
      '',
    );

    for (const [card, pattern] of Object.entries(
      PAYMENT.CREDIT_CARD_VALIDATION,
    )) {
      if (pattern.test(cardNumber)) {
        const cardImg =
          handleCardIcons(card) ||
          handleCardIcons(PAYMENT.CREDIT_CARD_TYPE.VISA);
        const cvvLen =
          card === PAYMENT.CREDIT_CARD_TYPE.AMERICAN_EXPRESS ? 4 : 3;
        const cardNumLen =
          card === PAYMENT.CREDIT_CARD_TYPE.AMERICAN_EXPRESS ? 15 : 16;
        setCardNumberLength(cardNumLen);
        setCvvLength(cvvLen);
        setCardType(card);
        setCardImg(cardImg);
        return card;
      } else {
        setCardType('');
        setCardImg('');
      }
    }
  }
  const handleCheckbox = () => {
    setIsDeliveryAdd(!isDeliveryAdd);
    if (cardInfo && Object.keys(cardInfo).length > 0) {
      dispatch(
        setBillingRequest({
          billingAddressFormData: cardInfo.billingAddress,
        }),
      );
    }
  };

  const validateField = (name: string, value: string): string => {
    let errorMessage = '';
    switch (name) {
      case CreditCardFormFields.CC_NUMBER: {
        if (!value) errorMessage = CHECKOUT_VALIDATION_MESSAGES.CC_NUM_MISSING;
        else {
          if (
            !cardType ||
            FIELD_VALIDATION_RULES.CREDIT_CARD_MIN_LENGTH > value.length
          )
            errorMessage = CHECKOUT_VALIDATION_MESSAGES.CC_NUM_INVALID;
          else {
            const creditCardType =
              Object.entries(FIELD_VALIDATION_RULES.CREDIT_CARD_NUMBER).find(
                ([key, regex]) => regex.test(value),
              )?.[0] || '';
            if (!creditCardType)
              errorMessage = CHECKOUT_VALIDATION_MESSAGES.CC_NUM_INVALID;
          }
        }
        break;
      }
      case CreditCardFormFields.CARD_CVV: {
        console.log(typeof value);
        if (!value) errorMessage = CHECKOUT_VALIDATION_MESSAGES.CC_CVV_MISSING;
        else if (!FIELD_VALIDATION_RULES.CREDIT_CARD_CVV.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.CC_CVV_INVALID;
        else if (cvvLength !== value.length)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.CC_CVV_INVALID;
        break;
      }
    }

    return errorMessage;
  };

  const updateFormData = (newState: Record<string, string>) => {
    return setFormData((prevState) => ({ ...prevState, ...newState }));
  };

  const handleFormUpdates = useCallback(
    (fieldType: CreditCardFormFields, event: ChangeEvent) => {
      let value = (event.target as HTMLInputElement).value.replace(/\D/g, '');
      setFormErrors((prev) => ({ ...prev, [fieldType]: '' }));
      updateFormData({ [fieldType]: value });
    },
    [],
  );

  const onBlurHandler = (
    key: string,
    e: React.FocusEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    let cardType = '';
    if (key === CreditCardFormFields.CC_NUMBER) {
      cardType = getCardType(e) || '';
    }
    const errorMessage = validateField(key, value);
    if (errorMessage) {
      setFormErrors((prev) => ({ ...prev, [key]: errorMessage }));
    }
    sendOnBlurDTMEvent({ key, errorMessage, value });
  };

  const handleSameBillingAddress = () => {
    if (cardInfo && Object.keys(cardInfo).length > 0) {
      dispatch(
        setBillingRequest({
          billingAddressFormData: cardInfo.billingAddress,
        }),
      );
    }
    dispatch(setBillingAddressError({}));
  };

  return (
    <form>
      <div className="row mb-3 g-ll-3 position-relative">
        <div className="col-12">
          <LLInput
            key={keyVal}
            isDisabled={keyVal !== PAYMENT.ADD_NEW_CC}
            name={CreditCardFormFields.CC_NUMBER}
            maxLength={cardNumberLength}
            value={
              keyVal !== PAYMENT.ADD_NEW_CC
                ? maskGiftCardNumber(formData[CreditCardFormFields.CC_NUMBER])
                : formData[CreditCardFormFields.CC_NUMBER]
            }
            errorMsg={formErrors?.[CreditCardFormFields.CC_NUMBER]}
            placeholder="Card Number"
            onChange={(e) => getCardType(e)}
            onBlur={(e) => onBlurHandler(CreditCardFormFields.CC_NUMBER, e)}
            automationId="co_bill_ccno"
            fsMask={true}
          />
          <div className="end-0 me-3 top-0 mt-2 position-absolute mt-ll-5">
            <Conditional if={cardType !== ''}>
              <Image
                src={cardImg || ''}
                alt={`${cardType} Card`}
                width={30}
                height={30}
                unoptimized={true}
              />
            </Conditional>
          </div>
        </div>
      </div>
      <div className="row g-ll-3">
        <div className="col-12 col-md-6">
          <ExpirationDateInput
            keyVal={`date-${keyVal}`}
            setFormData={setFormData}
            existValue={expDate}
            setFormErrors={setFormErrors}
            formError={formErrors['expirationError']}
            formErrors={formErrors}
          />
        </div>
        <div className="col-12 col-md-6 position-relative">
          <LLInput
            key={`cvv-${keyVal}`}
            placeholder="CVV"
            maxLength={cvvLength}
            name={CreditCardFormFields.CARD_CVV}
            value={formData[CreditCardFormFields.CARD_CVV]}
            errorMsg={formErrors?.[CreditCardFormFields.CARD_CVV]}
            onChange={(e) =>
              handleFormUpdates(CreditCardFormFields.CARD_CVV, e)
            }
            onBlur={(e) => onBlurHandler(CreditCardFormFields.CARD_CVV, e)}
            automationId="co_bill_cvv"
            fsMask={true}
          />
          <CVVHelpModal />
        </div>
      </div>
      <Conditional
        if={
          isShipOrder &&
          keyVal === PAYMENT.ADD_NEW_CC &&
          !registryAddressSelected &&
          !clearTheListAddressSelected
        }
      >
        <div className="row g-ll-3">
          <div className="col-12 pt-ll-3">
            <LLCheckbox
              key={keyVal}
              checked={isDeliveryAdd}
              testId="promoCheck"
              id={`promoCheck${keyVal}`}
              onChange={() => handleCheckbox()}
              automationId="co_shp_use_billing"
            >
              Use delivery address as billing address.
            </LLCheckbox>
          </div>
        </div>
      </Conditional>
      <Conditional if={!editBillingAddress && keyVal !== PAYMENT.ADD_NEW_CC}>
        <div className="row g-ll-3">
          <div className="col-12 pt-ll-3">
            <button
              className="btn btn-link"
              onClick={() => {
                setEditBillingAddress && setEditBillingAddress(true);
                handleSameBillingAddress();
              }}
            >
              Edit Billing Address
            </button>
          </div>
        </div>
      </Conditional>
    </form>
  );
};

export default CCCardDetailsForm;
